// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import translate from "@insite/client-framework/Translate";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import FormWrapper from "@store/Library/Components/Toolkit/FormWrapper";
import { formGridProps } from "@store/Themes/common/props/form";
import getInvalidFieldMessage from "@toolkit/Framework/Common/Utilities/forms/getInvalidFieldMessage";
import getRequiredFieldMessage from "@toolkit/Framework/Common/Utilities/forms/getRequiredFieldMessage";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import validatePhone from "@insite/client-framework/Common/Utilities/validatePhone";
import askForQuote from "@store/Framework/Store/Store/Components/AskForQuote/Handlers/AskForQuote";
import { css } from "styled-components";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import React from "react";

interface OwnProps {
    text?: string;
    extendedStyles?: AskForQuoteLinkStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasProduct;

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
    currentBillTo: getCurrentBillToState(state).value,
});

const mapDispatchToProps = {
    askForQuote,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: React.RefObject<ReCAPTCHA | null> },
        boolean
    >(validateReCaptcha),
};

export interface AskForQuoteLinkStyles {
    button?: ButtonPresentationProps;
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    lastNameGridItem?: GridItemProps;
    lastNameTextField?: TextFieldProps;
    phoneGridItem?: GridItemProps;
    phoneTextField?: TextFieldProps;
    stateGridItem?: GridItemProps;
    stateTextField?: TextFieldProps;
    cityGridItem?: GridItemProps;
    cityTextField?: TextFieldProps;
    companyGridItem?: GridItemProps;
    companyTextField?: TextFieldProps;
    firstNameGridItem?: GridItemProps;
    firstNameTextField?: TextFieldProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldProps;
    commentsGridItem?: GridItemProps;
    commentsTextArea?: TextAreaProps;
    reCaptchaGridItem?: GridItemProps;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const askForQuoteLinkStyles: AskForQuoteLinkStyles = {
    modal: {
        sizeVariant: "medium",
    },
    button: {
        variant: "primary",
        css: css`
            ${x => x.theme.button.primary.css}
        `,
    },
    container: formGridProps.gridContainer,
    lastNameGridItem: {
        width: [12, 6, 6, 6, 6],
    },
    companyGridItem: {
        width: [12, 6, 6, 6, 6],
    },
    firstNameGridItem: {
        width: [12, 6, 6, 6, 6],
    },
    phoneGridItem: {
        width: [12, 6, 6, 6, 6],
    },
    stateGridItem: {
        width: [12, 6, 6, 6, 6],
    },
    cityGridItem: {
        width: [12, 6, 6, 6, 6],
    },
    emailGridItem: {
        width: [12, 6, 6, 6, 6],
    },
    commentsGridItem: {
        width: 12,
    },
    reCaptchaGridItem: {
        width: 12,
    },
    cancelButton: {
        variant: "secondary",
    },
};

const AskForQuoteLink = ({
    product,
    text,
    session,
    currentBillTo,
    askForQuote,
    validateReCaptcha,
    extendedStyles,
}: Props) => {
    const toasterContext = React.useContext(ToasterContext);
    const [styles] = React.useState(() => mergeToNew(askForQuoteLinkStyles, extendedStyles));
    const isAuthenticated = (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const initialFirstName = session!.firstName || "";
    const [firstName, setFirstName] = React.useState(initialFirstName);
    const [firstNameError, setFirstNameError] = React.useState("");
    const initialLastName = session!.lastName || "";
    const [lastName, setLastName] = React.useState(initialLastName);
    const [lastNameError, setLastNameError] = React.useState("");
    const initialProductSku = product.productNumber || "";
    const [productSku] = React.useState(initialProductSku);
    const initialPhone = currentBillTo?.phone || "";
    const [phone, setPhone] = React.useState(initialPhone);
    const [phoneError, setPhoneError] = React.useState("");
    const initialState = currentBillTo && currentBillTo?.state ? currentBillTo?.state.name : "";
    const [state, setState] = React.useState(initialState);
    const [stateError, setStateError] = React.useState("");
    const initialCity = currentBillTo?.city || "";
    const [city, setCity] = React.useState(initialCity);
    const [cityError, setCityError] = React.useState("");
    const initialCompany = currentBillTo?.companyName ? currentBillTo?.companyName : "";
    const [company, setCompany] = React.useState("");
    const [companyError, setCompanyError] = React.useState("");
    const initialEmail = isAuthenticated ? session.email || "" : "";
    const [email, setEmail] = React.useState(initialEmail);
    const [emailError, setEmailError] = React.useState("");

    const initialComments = initialProductSku ? translate("Quote request for {0}", initialProductSku) : "";
    const [comments, setComments] = React.useState(initialComments);
    const [commentsError, setCommentsError] = React.useState("");
    const [inProgress, setInProgress] = React.useState(false);
    const recaptchaRef: React.RefObject<ReCAPTCHA | null> = React.createRef();

    const emailLabel = translate("Your Email");
    const firstNameLabel = translate("First Name");
    const lastNameLabel = translate("Last Name");
    const companyLabel = translate("Company");
    const phoneLabel = translate("Phone");
    const stateLabel = translate("State");
    const cityLabel = translate("City");
    const commentsLabel = translate("Your Message");

    React.useEffect(() => {
        if (isAuthenticated) {
            if (!currentBillTo) {
                loadCurrentBillTo();
            }
        }
    }, []);

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const shareLinkClickHandler = () => {
        resetFields();
        setModalIsOpen(true);
    };

    const checkForErrors = () => {
        const localFirstNameError = !firstName ? getRequiredFieldMessage(firstNameLabel) : "";
        setFirstNameError(localFirstNameError);

        const localLastNameError = !lastName ? getRequiredFieldMessage(lastNameLabel) : "";
        setLastNameError(localLastNameError);

        const localEmailError = !email
            ? getRequiredFieldMessage(emailLabel)
            : validateEmail(email)
              ? ""
              : getInvalidFieldMessage(emailLabel);
        setEmailError(localEmailError);

        const localCompanyError = !company ? getRequiredFieldMessage(companyLabel) : "";
        setCompanyError(localCompanyError);

        const localPhoneError = !phone
            ? getRequiredFieldMessage(phoneLabel)
            : validatePhone(phone)
              ? ""
              : getInvalidFieldMessage(phoneLabel);
        setPhoneError(localPhoneError);

        const localStateError = !state ? getRequiredFieldMessage(stateLabel) : "";
        setStateError(localStateError);

        const localCityError = !city ? getRequiredFieldMessage(cityLabel) : "";
        setCityError(localCityError);

        const localCommentsError = !comments ? getRequiredFieldMessage(commentsLabel) : "";
        setCommentsError(localCommentsError);

        return !!(
            localFirstNameError ||
            localLastNameError ||
            localCompanyError ||
            localEmailError ||
            localPhoneError ||
            localStateError ||
            localCityError ||
            localCommentsError
        );
    };

    const resetFields = () => {
        setFirstName(initialFirstName);
        setFirstNameError("");

        setLastName(initialLastName);
        setLastNameError("");

        setCompany(initialCompany);
        setCompanyError("");

        setEmail(initialEmail);
        setEmailError("");

        setPhone(initialPhone);
        setPhoneError("");

        setState(initialState);
        setStateError("");

        setCity(initialCity);
        setCityError("");

        setComments(initialComments);
        setCommentsError("");
        setInProgress(false);
    };

    const lastNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLastName(event.target.value);
        setLastNameError(!event.target.value.trim() ? getRequiredFieldMessage(lastNameLabel) : "");
    };
    const phoneChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPhone(event.target.value);
        setPhoneError(!event.target.value.trim() ? getRequiredFieldMessage(phoneLabel) : "");
    };
    const stateChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState(event.target.value);
        setStateError(!event.target.value.trim() ? getRequiredFieldMessage(stateLabel) : "");
    };
    const cityChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCity(event.target.value);
        setCityError(!event.target.value.trim() ? getRequiredFieldMessage(cityLabel) : "");
    };
    const companyChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCompany(event.target.value);
        setCompanyError(!event.target.value.trim() ? getRequiredFieldMessage(companyLabel) : "");
    };
    const firstNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFirstName(event.target.value);
        setFirstNameError(!event.target.value.trim() ? getRequiredFieldMessage(firstNameLabel) : "");
    };
    const emailChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(event.target.value);
        setEmailError(!event.target.value.trim() ? getRequiredFieldMessage(emailLabel) : "");
    };
    const commentsChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setComments(event.target.value);
        setCommentsError(!event.target.value.trim() ? getRequiredFieldMessage(commentsLabel) : "");
    };

    const submitButtonClickHandler = async () => {
        if (checkForErrors()) {
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({
            location: "ContactUs",
            recaptchaRef,
        });

        if (!isReCaptchaValid) {
            return;
        }

        setInProgress(true);

        askForQuote({
            sku: productSku,
            firstName,
            lastName,
            email,
            company,
            phone,
            province: state,
            city,
            comments,
            onSuccess: () => {
                setModalIsOpen(false);
                toasterContext.addToast({ body: siteMessage("AskForQuote_Success"), messageType: "success" });
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onSuccess?.();
                }
            },
        });
    };

    return (
        <>
            <Button {...styles.button} onClick={shareLinkClickHandler} data-test-selector="askForQuoteLink">
                {text || translate("Request for quote")}
            </Button>
            <Modal
                {...styles.modal}
                headline={translate("Request for quote")}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
            >
                <FormWrapper onlyFocus>
                    <GridContainer {...styles.container} data-test-selector="askForQuoteModal">
                        <GridItem {...styles.firstNameGridItem}>
                            <TextField
                                label={firstNameLabel}
                                value={firstName}
                                error={firstNameError}
                                onChange={firstNameChangeHandler}
                                data-test-selector="askForQuoteFirstName"
                                required
                                {...styles.firstNameTextField}
                            />
                        </GridItem>
                        <GridItem {...styles.lastNameGridItem}>
                            <TextField
                                label={lastNameLabel}
                                value={lastName}
                                error={lastNameError}
                                onChange={lastNameChangeHandler}
                                data-test-selector="askForQuoteLastName"
                                required
                                {...styles.lastNameTextField}
                            />
                        </GridItem>
                        <GridItem {...styles.companyGridItem}>
                            <TextField
                                label={companyLabel}
                                value={company}
                                error={companyError}
                                onChange={companyChangeHandler}
                                data-test-selector="askForQuoteCompany"
                                maxLength={254}
                                required
                                {...styles.companyTextField}
                            />
                        </GridItem>
                        <GridItem {...styles.phoneGridItem}>
                            <TextField
                                label={phoneLabel}
                                value={phone}
                                error={phoneError}
                                onChange={phoneChangeHandler}
                                data-test-selector="askForQuotePhone"
                                required
                                {...styles.phoneTextField}
                            />
                        </GridItem>
                        <GridItem {...styles.stateGridItem}>
                            <TextField
                                label={stateLabel}
                                value={state}
                                error={stateError}
                                onChange={stateChangeHandler}
                                data-test-selector="askForQuoteState"
                                required
                                {...styles.stateTextField}
                            />
                        </GridItem>
                        <GridItem {...styles.cityGridItem}>
                            <TextField
                                label={cityLabel}
                                value={city}
                                error={cityError}
                                onChange={cityChangeHandler}
                                data-test-selector="askForQuoteCity"
                                required
                                {...styles.cityTextField}
                            />
                        </GridItem>
                        <GridItem {...styles.emailGridItem}>
                            <TextField
                                label={emailLabel}
                                value={email}
                                error={emailError}
                                onChange={emailChangeHandler}
                                data-test-selector="askForQuoteEmail"
                                required
                                {...styles.emailTextField}
                            />
                        </GridItem>
                        <GridItem {...styles.commentsGridItem}>
                            <TextArea
                                label={commentsLabel}
                                value={comments}
                                error={commentsError}
                                onChange={commentsChangeHandler}
                                data-test-selector="askForQuoteComments"
                                required
                                {...styles.commentsTextArea}
                            />
                        </GridItem>
                    </GridContainer>
                    <ReCaptcha recaptchaRef={recaptchaRef} location="ContactUs" />
                    <FormActions>
                        <Button
                            {...styles.submitButton}
                            onClick={submitButtonClickHandler}
                            data-test-selector="askForQuoteSubmit"
                            disabled={inProgress}
                        >
                            {translate("Submit")}
                        </Button>
                        <Button
                            {...styles.cancelButton}
                            onClick={modalCloseHandler}
                            data-test-selector="askForQuoteCancel"
                        >
                            {translate("Cancel")}
                        </Button>
                    </FormActions>
                </FormWrapper>
            </Modal>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProduct(AskForQuoteLink));
